
import { values } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

import React from "react";

const Batteries = () => {
  return (
    <div>
      {/* Page Header */}
      <div className="bg-gray-800 py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white text-center">
            Battery
          </h1>
        </div>
      </div>

      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* HERO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-28">
            <div className="relative group">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQi4yNPiPGlDtwEBYnWlKEsLdnLOO8m-V7lBw&s"
                className="rounded-3xl shadow-2xl object-cover w-full h-96 transition-transform duration-300 group-hover:scale-105"
                alt="Battery refurbishment"
              />
              <div className="absolute inset-0 bg-black/10 rounded-3xl "></div>
            </div>

            <div className="flex flex-col justify-center">
              <h1 className="text-5xl font-extrabold mb-6 leading-tight">
                Lead-Acid Battery Refurbishment
              </h1>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                Revive weak & dead batteries with our patent-pending,
                minimally-invasive technology that cuts costs and reduces
                environmental impact.
              </p>

              <ul className="space-y-3 text-gray-800">
                <li className="flex items-center gap-2">⚡ Restore performance</li>
                <li className="flex items-center gap-2">⚡ Reduce costs up to 70%</li>
                <li className="flex items-center gap-2">⚡ Extend life by 1–2 years</li>
                <li className="flex items-center gap-2">⚡ Eco-friendly & sustainable</li>
              </ul>
            </div>
          </div>

          {/* WHAT IS REFURBISHMENT */}
          <h2 className="text-4xl font-bold mb-6">What is Battery Refurbishment?</h2>

          <p className="text-gray-700 mb-14 max-w-4xl leading-relaxed">
            Battery refurbishment is an advanced scientific method used to revive
            weak or dead lead-acid batteries by reversing sulphation, restoring
            electrolyte balance, and rejuvenating internal plates.
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
            {[
              "Better backup time",
              "Restored efficiency",
              "1–2 years extended life",
              "Cheaper than buying new",
            ].map((item, i) => (
              <li
                key={i}
                className="p-6 bg-white/70 backdrop-blur-xl shadow-xl border border-gray-200 rounded-2xl text-center font-semibold"
              >
                ✔ {item}
              </li>
            ))}
          </ul>

          {/* WHY BATTERIES FAIL */}
          <h2 className="text-4xl font-bold mb-8">Why Batteries Fail?</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-28">
            {[
              ["Sulphation", "Crystals form on plates & block reactions."],
              ["Electrolyte Weakening", "Charging issues reduce acid strength."],
              ["Plate Corrosion", "Reduces battery efficiency."],
              ["Overcharging", "Accelerates battery ageing."],
            ].map(([title, desc]) => (
              <div
                key={title}
                className="p-6 bg-white shadow-xl rounded-2xl border border-gray-100 hover:shadow-2xl transition"
              >
                <h4 className="font-bold mb-2">{title}</h4>
                <p className="text-gray-600">{desc}</p>
              </div>
            ))}
          </div>

          {/* PROCESS */}
          <h2 className="text-4xl font-bold mb-10 text-center">
            Our Refurbishment Process
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-28 text-center">
            {[
              ["On-Site Visit", "We collect weak/dead batteries."],
              ["Inspection", "Clean, test & inspect issues."],
              ["Chemical Treatment", "Apply desulphation solution."],
              ["Smart Charging", "PWM charging cycles."],
              ["Load Testing", "Run backup & load tests."],
              ["Final Charge", "Restore peak performance."],
            ].map(([title, desc]) => (
              <div
                key={title}
                className="p-6 bg-white shadow-xl rounded-xl border hover:shadow-2xl transition"
              >
                <h4 className="font-semibold mb-2">{title}</h4>
                <p className="text-gray-600 text-sm">{desc}</p>
              </div>
            ))}
          </div>

          {/* TECHNOLOGY */}
          <h2 className="text-4xl font-bold mb-10">
            Technology Behind Refurbishment
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-28">
            <img
              src="https://cdn.pixabay.com/photo/2016/08/02/17/38/car-1564300_1280.jpg"
              className="rounded-3xl shadow-2xl w-full h-80 object-cover"
              alt="Battery plates"
            />

            <div>
              <h3 className="font-bold text-2xl mb-3">Sulphate Layer Formation</h3>
              <p className="text-gray-700 mb-6">
                PbSO₄ crystal layers block electrolyte interaction, reducing
                battery output.
              </p>

              <h3 className="font-bold text-2xl mb-3">
                Sulphate Removal{" "}
                <span className="text-yellow-600">(Desulphation)</span>
              </h3>
              <p className="text-gray-700 mb-6">
                Our proprietary chemical & charging technology dissolves
                sulphation and restores energy flow.
              </p>

              <ul className="space-y-2 text-gray-800">
                <li>✔ Better energy storage</li>
                <li>✔ Faster charging</li>
                <li>✔ Longer backup time</li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center py-20 bg-white shadow-xl rounded-3xl border">
            <h2 className="text-4xl font-bold mb-4">
              Want to reduce battery costs by up to 70%?
            </h2>
            <p className="text-gray-600 mb-8">
              Contact our refurbishment experts today.
            </p>

            <a
              href="/contact"
              className="bg-yellow-600 hover:bg-yellow-700 text-white py-4 px-10 rounded-xl text-lg shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Batteries;
